import { motion } from "motion/react";
import { ChevronRight } from "lucide-react";
import type { AgentDefinition } from "@/data/transparency";
import { getAgentIcon } from "@/data/transparency";

interface AgentCardProps {
  agent: AgentDefinition;
  onClick: () => void;
  isActive?: boolean;
  delay?: number;
}

export default function AgentCard({ agent, onClick, isActive = false, delay = 0 }: AgentCardProps) {
  const Icon = getAgentIcon(agent.icon);

  return (
    <motion.button
      onClick={onClick}
      className={`
        group relative w-full h-full min-h-[280px] text-left p-6 rounded-2xl border transition-all duration-500
        bg-white hover:border-[#D4893E]/40 hover:shadow-md
        ${isActive 
          ? "border-[#D4893E]/50 shadow-md" 
          : "border-gray-200"
        }
      `}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ delay, duration: 0.6 }}
      whileHover={{ y: -8 }}
    >
      <div className="relative z-10">
        {/* Icon Container */}
        <div 
          className="w-14 h-14 rounded-xl flex items-center justify-center mb-4 transition-transform group-hover:scale-110"
          style={{ backgroundColor: `${agent.color}20` }}
        >
          <Icon 
            className="w-7 h-7" 
            style={{ color: agent.color }}
          />
        </div>

        {/* Content */}
        <div className="space-y-2">
          <div>
            <span 
              className="text-xs font-medium uppercase tracking-wider"
              style={{ color: agent.color }}
            >
              {agent.subtitle}
            </span>
            <h3 className="text-xl font-semibold text-[#063F6B] mt-1">
              {agent.name}
            </h3>
          </div>
          
          <p className="text-sm text-gray-600 leading-relaxed">
            {agent.description}
          </p>

          {/* Bottom row: Model Badge + View Details */}
          <div className="pt-3 flex items-center justify-between">
            {agent.model ? (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-[#063F6B]/10 text-[#063F6B]">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                {agent.model}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
                <span className="w-1.5 h-1.5 rounded-full bg-gray-400" />
                No AI Model
              </span>
            )}
            
            {/* View Details Indicator */}
            <div className="flex items-center gap-1 text-sm font-medium text-[#D4893E] opacity-0 group-hover:opacity-100 transition-opacity">
              View details
              <ChevronRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            </div>
          </div>
        </div>
      </div>
    </motion.button>
  );
}
